/**
 * THE WORK BOARD. Built to work the day, not to display it.
 *
 * Mail-client shape: a queue rail on the left with real counts, the chosen queue as a full list
 * in the middle, and a work pane on the right that carries the next step for whatever is
 * selected. Every queue opens to its whole list, every item has an action, and the crew do the
 * work they are named for.
 *
 * Three rules from the rest of MO OS hold here without exception. A commitment is closed only
 * through `closeCommitment`, and the note records it was closed by him, not by evidence. A chase
 * has NO "sent" state: `chaseDraftPass.ts` advances a chase when mail actually moves, so the button
 * is "Sent it, hold 7 days" and it is a snooze. And MO OS never sends mail as him: the draft is
 * copied to the clipboard and that is the whole extent of it.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type {
  Dashboard,
  DashboardChase,
  DashboardCommitment,
  DashboardMeeting,
  DashboardObjective,
  DashboardUpdate,
} from '@rbv/shared';
import { closeCommitment, dropChase, snoozeChase } from './api.js';
import { ViewPicker } from './CommandDeck.js';
import { openAsk } from './ChatOverlay.js';
import { useReveal } from './MoDashboard.js';
import { useAssurance } from './useAssurance.js';
import type { DeckView } from './views.js';
import './workBoard.css';

type QueueKey = 'now' | 'owe' | 'chase' | 'week' | `obj:${number}` | 'feed' | 'machine';

type Row =
  | { kind: 'meeting'; m: DashboardMeeting }
  | { kind: 'commitment'; c: DashboardCommitment }
  | { kind: 'chase'; ch: DashboardChase }
  | { kind: 'engagement'; name: string; objective: DashboardObjective }
  | { kind: 'update'; u: DashboardUpdate }
  | { kind: 'lane'; lane: string; label: string; state: string; why: string; at: string | null };

interface Queue {
  key: QueueKey;
  label: string;
  count: string;
  title: string;
  sub: string;
  rows: Row[];
}

type Outcome = { ok: true } | { ok: false; error: string };

const isoPlusDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

const when = (iso: string, opts: Intl.DateTimeFormatOptions) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', opts);
};

export function WorkBoard({
  dashboard: d,
  onPick,
  onReload,
}: {
  dashboard: Dashboard;
  onPick: (v: DeckView) => void;
  onReload: () => void;
}): JSX.Element {
  const assurance = useAssurance();
  /* Opens the note when the vault has it, else searches its title. The deck's own rule. */
  const reveal = useReveal();
  const [q, setQ] = useState<QueueKey>('now');
  const [sel, setSel] = useState(0);
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);

  const queues = useMemo<Queue[]>(() => {
    const chases = d.chases ?? [];
    const today = d.agenda.filter((m) => m.isToday).length;
    return [
      {
        key: 'now',
        label: 'Next meeting',
        count: String(today),
        title: 'Next meeting',
        sub: `${today} today · ${d.agenda.length} ahead`,
        rows: d.agenda.slice(0, 1).map((m) => ({ kind: 'meeting', m })),
      },
      {
        key: 'owe',
        label: 'You owe',
        count: String(d.owedTotal),
        title: 'You owe',
        sub: `${d.owed.length} shown of ${d.owedTotal} · soonest first`,
        rows: d.owed.map((c) => ({ kind: 'commitment', c })),
      },
      {
        key: 'chase',
        label: 'Chase queue',
        count: d.chases === undefined ? '?' : String(chases.length),
        title: 'Chase queue',
        sub:
          d.chases === undefined
            ? 'the CRM did not answer'
            : `${chases.length} accounts · ${chases.filter((c) => c.draft?.body).length} drafts ready`,
        rows: chases.map((ch) => ({ kind: 'chase', ch })),
      },
      {
        key: 'week',
        label: 'Ahead',
        count: String(d.agenda.length),
        title: 'Ahead',
        sub: `${d.agenda.length} meetings on the calendar`,
        rows: d.agenda.map((m) => ({ kind: 'meeting', m })),
      },
      ...d.intent.map(
        (o, i): Queue => ({
          key: `obj:${i}`,
          label: o.name,
          count: o.staleDays === null ? '—' : `${o.staleDays}d`,
          title: o.name,
          sub: `${o.engagements.length} engagements · ${o.openCommitments} open · ${
            o.staleDays === null ? 'no movement dated' : `moved ${o.staleDays}d ago`
          }`,
          rows: o.engagements.map((name) => ({ kind: 'engagement', name, objective: o })),
        }),
      ),
      {
        key: 'feed',
        label: 'Since you checked',
        count: String(d.updates.length),
        title: 'Since you last checked',
        sub: `${d.updates.length} changes`,
        rows: d.updates.map((u) => ({ kind: 'update', u })),
      },
      {
        key: 'machine',
        label: 'The machine',
        count: assurance ? `${assurance.lanes.filter((l) => l.state !== 'healthy').length} fault` : '?',
        title: 'The machine',
        sub: assurance ? `${assurance.lanes.length} lanes` : 'assurance not reported',
        rows: (assurance?.lanes ?? []).map((l) => ({
          kind: 'lane',
          lane: l.lane,
          label: l.label,
          state: l.state,
          why: l.why,
          at: l.last?.at ?? null,
        })),
      },
    ];
  }, [d, assurance]);

  const queue = queues.find((x) => x.key === q) ?? queues[0]!;
  const row = queue.rows[sel] ?? null;

  const go = useCallback((k: QueueKey) => {
    setQ(k);
    setSel(0);
    setToast('');
  }, []);

  const run = useCallback(
    async (label: string, p: Promise<Outcome>) => {
      setBusy(true);
      const r = await p;
      setBusy(false);
      /*
       * Shown, not swallowed, and the payload is NOT reloaded on failure. Reloading would redraw
       * the row unchanged and read as the click having done nothing at all, which is the state
       * that leaves him believing something is closed when it is not.
       */
      if (r.ok) {
        setToast(label);
        onReload();
      } else {
        setToast(`Did not land: ${r.error}`);
      }
    },
    [onReload],
  );

  const doneCommitment = useCallback(
    (c: DashboardCommitment) =>
      run('Marked done. Closed by you, not by evidence; the note says so.', closeCommitment(c.path)),
    [run],
  );
  const hold = useCallback(
    (ch: DashboardChase, days: number) =>
      run(`Held ${days} days. MO OS notices when the mail moves.`, snoozeChase(ch.account, isoPlusDays(days))),
    [run],
  );
  const drop = useCallback((ch: DashboardChase, why: string) => run('Dropped from the queue.', dropChase(ch.account, why)), [run]);
  const copyDraft = useCallback(async (ch: DashboardChase) => {
    const b = ch.draft?.body;
    if (!b) return;
    try {
      await navigator.clipboard.writeText(ch.draft?.subject ? `${ch.draft.subject}\n\n${b}` : b);
      setToast('Copied. Paste into Outlook; MO OS does not send as you.');
    } catch {
      setToast('Could not reach the clipboard. Select the draft and copy it.');
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'j') setSel((s) => Math.min(Math.max(0, queue.rows.length - 1), s + 1));
      else if (e.key === 'k') setSel((s) => Math.max(0, s - 1));
      else if (e.key === 'd' && row?.kind === 'commitment') void doneCommitment(row.c);
      else if (e.key === 'c' && row?.kind === 'chase') void copyDraft(row.ch);
      else if (/^[1-9]$/.test(e.key)) {
        const k = queues[Number(e.key) - 1];
        if (k) go(k.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [queue, queues, row, doneCommitment, copyDraft, go]);

  const openNote = (path: string) => reveal(path);

  const group = (keys: (k: QueueKey) => boolean) =>
    queues.filter((x) => keys(x.key)).map((x) => <QueueButton key={x.key} q={x} on={x.key === q} go={go} />);

  return (
    <main className="mo-dash wb" aria-label="Work board">
      <header className="wb-top">
        <div className="wb-mark">
          <img src="/deck-emblem.webp" alt="" aria-hidden="true" />
          <div>
            <p className="wb-wordmark">MO OS</p>
            <p className="wb-wordsub">work board</p>
          </div>
        </div>
        <p className="wb-greet">
          {greeting()}{' '}
          <span>{when(d.generatedAt, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</span>
        </p>
        <div className="wb-controls">
          <ViewPicker current="work" onPick={onPick} />
        </div>
      </header>

      <nav className="wb-rail" aria-label="Queues">
        <p className="wb-h4">Today</p>
        {group((k) => ['now', 'owe', 'chase', 'week'].includes(k))}
        <p className="wb-h4">Objectives</p>
        {group((k) => k.startsWith('obj:'))}
        {d.intent.length === 0 && <p className="wb-none">None declared.</p>}
        <p className="wb-h4">Everything</p>
        {group((k) => ['feed', 'machine'].includes(k))}
        <div className="wb-mo" aria-hidden="true">
          <img className="wb-mo-s" src="/deck-mo.webp" alt="" />
          <img className="wb-mo-e" src="/deck-emblem.webp" alt="" />
          <p>
            MO · <b>on watch</b>
          </p>
        </div>
        <p className="wb-keys">
          <kbd>j</kbd>
          <kbd>k</kbd> move <kbd>d</kbd> done <kbd>c</kbd> copy <kbd>1</kbd>-<kbd>9</kbd> queue
        </p>
      </nav>

      <section className="wb-list">
        <div className="wb-head">
          <h2>{queue.title}</h2>
          <span>{queue.sub}</span>
        </div>
        {queue.rows.length === 0 ? (
          <p className="wb-none">Nothing here.</p>
        ) : (
          <div className="wb-rows">
            {queue.rows.map((r, i) => (
              <button
                key={i}
                type="button"
                className="wb-row"
                data-sel={i === sel || undefined}
                onClick={() => {
                  setSel(i);
                  setToast('');
                }}
              >
                <span className="wb-idx">{i + 1}</span>
                <span>
                  <span className="wb-t">{rowTitle(r)}</span>
                  <span className="wb-m">{rowMeta(r)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <aside className="wb-work" aria-live="polite">
        {row === null ? (
          <p className="wb-none">Select a row to work it.</p>
        ) : (
          <WorkPane
            row={row}
            busy={busy}
            toast={toast}
            onDone={doneCommitment}
            onHold={hold}
            onDrop={drop}
            onCopy={copyDraft}
            onOpen={openNote}
          />
        )}
      </aside>
    </main>
  );
}

function QueueButton({ q, on, go }: { q: Queue; on: boolean; go: (k: QueueKey) => void }): JSX.Element {
  return (
    <button type="button" className="wb-q" data-q={q.key} data-on={on || undefined} onClick={() => go(q.key)}>
      <span className="wb-q-l">{q.label}</span>
      <span className="wb-q-n">{q.count}</span>
    </button>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning, Mo' : h < 18 ? 'Good afternoon, Mo' : 'Good evening, Mo';
}

function rowTitle(r: Row): string {
  switch (r.kind) {
    case 'meeting':
      return r.m.title;
    case 'commitment':
      return r.c.text;
    case 'chase':
      return `${r.ch.account} · ${r.ch.line}`;
    case 'engagement':
      return r.name;
    case 'update':
      return r.u.title;
    case 'lane':
      return r.label;
  }
}

function rowMeta(r: Row): string {
  switch (r.kind) {
    case 'meeting':
      return `${when(r.m.startsAt, { weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })} · ${
        r.m.organiser
      }${r.m.recurring ? ' · repeats' : ''}`;
    case 'commitment':
      return `you owe${r.c.counterparty ? ` · ${r.c.counterparty}` : ''}${
        r.c.objective ? ` · serves ${r.c.objective}` : ''
      }${r.c.due ? ` · due ${r.c.due}` : ''}`;
    case 'chase':
      return `${r.ch.draft?.body ? 'draft ready' : r.ch.draft ? 'nothing to say yet' : 'not drafted yet'} · to ${
        r.ch.recipient ?? r.ch.recipientNote ?? 'nobody on record'
      } · tier ${r.ch.tier}`;
    case 'engagement':
      return `serves ${r.objective.name}`;
    case 'update':
      return `${r.u.kind} · ${r.u.at.slice(0, 16).replace('T', ' ')}`;
    case 'lane':
      return `${r.state}${r.at ? ` · ${r.at.slice(0, 16).replace('T', ' ')}` : ''}`;
  }
}

function WorkPane({
  row,
  busy,
  toast,
  onDone,
  onHold,
  onDrop,
  onCopy,
  onOpen,
}: {
  row: Row;
  busy: boolean;
  toast: string;
  onDone: (c: DashboardCommitment) => void;
  onHold: (ch: DashboardChase, days: number) => void;
  onDrop: (ch: DashboardChase, why: string) => void;
  onCopy: (ch: DashboardChase) => void;
  onOpen: (path: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  /*
   * WHY THE DROP BUTTON IS NOT DISABLED, and it used to be.
   *
   * It was the ONLY control on this board disabled by default: every other button is disabled only
   * while a request is in flight. So it sat greyed out until a reason was typed, and said nothing
   * about why, which is indistinguishable from broken. He reported it as "the Drop button doesn't
   * work" on 2026-09-15, and the snooze beside it had been working from the same surface all along.
   *
   * A control that refuses has to say so. It now stays pressable, and an empty reason moves the
   * cursor into the box and explains, rather than silently doing nothing.
   */
  const [hint, setHint] = useState('');
  const reasonRef = useRef<HTMLInputElement>(null);
  switch (row.kind) {
    case 'meeting': {
      const m = row.m;
      return (
        <>
          <p className="wb-kick">Meeting · {when(m.startsAt, { weekday: 'long', hour: '2-digit', minute: '2-digit' })}</p>
          <h3>{m.title}</h3>
          <p className="wb-p">
            {m.organiser}
            {m.recurring ? ' · repeats' : ''}
          </p>
          <Crew
            who="zou"
            name="Zou 象"
            role="create & compose"
            line="Can prepare a first-meeting plan from the vault. Anything that leaves the building follows the Vantage design."
          />
          <div className="wb-btns">
            <button
              type="button"
              className="wb-btn wb-primary"
              onClick={() =>
                openAsk({
                  lane: 'zou',
                  about: `Prepare me for "${m.title}". Who is in the room, what the vault holds on them, and what I should open with.`,
                })
              }
            >
              Ask Zou to prepare
            </button>
            <button type="button" className="wb-btn" onClick={() => onOpen(m.path)}>
              Open the note
            </button>
          </div>
          <p className="wb-toast">{toast}</p>
        </>
      );
    }
    case 'commitment': {
      const c = row.c;
      return (
        <>
          <p className="wb-kick">Commitment · you owe</p>
          <h3>{c.text}</h3>
          <dl className="wb-facts">
            <div>
              <dt>Counterparty</dt>
              <dd>{c.counterparty || 'not recorded'}</dd>
            </div>
            <div>
              <dt>Serves</dt>
              <dd>{c.objective || 'no objective · unattributed'}</dd>
            </div>
            <div>
              <dt>Topic</dt>
              <dd>{c.topic || 'none'}</dd>
            </div>
            <div>
              <dt>Due</dt>
              <dd>{c.due || 'none stated · not inferred'}</dd>
            </div>
          </dl>
          {!c.objective && (
            <Crew
              who="hermes"
              name="Fawzy"
              role="capture & automate"
              line="No topic on this one, so it counts toward no objective. Forward the thread it came from and it will."
            />
          )}
          <div className="wb-btns">
            <button type="button" className="wb-btn wb-primary" disabled={busy} onClick={() => onDone(c)}>
              Mark done
            </button>
            <button
              type="button"
              className="wb-btn"
              onClick={() => openAsk({ lane: 'zou', about: `Draft my message to ${c.counterparty || 'them'} about: ${c.text}` })}
            >
              Draft the message
            </button>
            <button type="button" className="wb-btn" onClick={() => onOpen(c.path)}>
              Open the note
            </button>
          </div>
          <p className="wb-toast">{toast}</p>
        </>
      );
    }
    case 'chase': {
      const ch = row.ch;
      const dr = ch.draft;
      return (
        <>
          <p className="wb-kick">
            Chase · tier {ch.tier} · {ch.daysQuiet} days quiet
          </p>
          <h3>{ch.account}</h3>
          <p className="wb-p">
            {ch.line} · write to {ch.recipient ?? ch.recipientNote ?? 'nobody on record'}
          </p>
          {dr?.body ? (
            <pre className="wb-draft">
              {dr.subject ? `${dr.subject}\n\n` : ''}
              {dr.body}
            </pre>
          ) : dr ? (
            <p className="wb-none">No draft: {dr.why}</p>
          ) : (
            <p className="wb-none">The drafting pass has not reached this account yet.</p>
          )}
          <Crew
            who="zou"
            name="Zou 象"
            role="create & compose"
            line={
              dr?.grounding === 'web'
                ? 'Cold approach, so the hook comes from a web lookup on the company name. Check the source before you send.'
                : 'Drafted from what MO OS has read. It never sends as you: copy it into Outlook and hold the queue.'
            }
          />
          <div className="wb-btns">
            {dr?.body && (
              <button type="button" className="wb-btn wb-primary" data-do="copy" onClick={() => void onCopy(ch)}>
                Copy the email
              </button>
            )}
            <button type="button" className="wb-btn" data-do="hold7" disabled={busy} onClick={() => onHold(ch, 7)}>
              Sent it, hold 7 days
            </button>
            <button type="button" className="wb-btn" data-do="hold30" disabled={busy} onClick={() => onHold(ch, 30)}>
              Not now, 30 days
            </button>
            <button
              type="button"
              className="wb-btn"
              onClick={() => openAsk({ lane: 'zou', about: `Rework the follow-up to ${ch.account} for ${ch.recipient ?? 'them'}.` })}
            >
              Edit with Zou
            </button>
          </div>
          <div className="wb-drop">
            <input
              ref={reasonRef}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (hint !== '') setHint('');
              }}
              placeholder="Why drop it"
              aria-label="Reason to drop"
            />
            <button
              type="button"
              className="wb-btn"
              disabled={busy}
              onClick={() => {
                if (reason.trim() === '') {
                  setHint('Tell me why first. The reason is shown back to you beside the row.');
                  reasonRef.current?.focus();
                  return;
                }
                setHint('');
                onDrop(ch, reason.trim());
              }}
            >
              Drop
            </button>
          </div>
          <p className="wb-toast">{hint === '' ? toast : hint}</p>
        </>
      );
    }
    case 'engagement':
      return (
        <>
          <p className="wb-kick">Engagement</p>
          <h3>{row.name}</h3>
          <p className="wb-p">
            Serves {row.objective.name}. Per-engagement state is not on this payload; the topic note holds it.
          </p>
          <div className="wb-btns">
            <button
              type="button"
              className="wb-btn wb-primary"
              onClick={() =>
                openAsk({
                  lane: 'brain',
                  about: `What is the state of ${row.name}? Last contact, open commitments, who owes whom.`,
                })
              }
            >
              Ask the brain
            </button>
          </div>
        </>
      );
    case 'update':
      return (
        <>
          <p className="wb-kick">{row.u.kind}</p>
          <h3>{row.u.title}</h3>
          <div className="wb-btns">
            <button type="button" className="wb-btn wb-primary" onClick={() => onOpen(row.u.path)}>
              Open the note
            </button>
          </div>
        </>
      );
    case 'lane':
      return (
        <>
          <p className="wb-kick">Lane · {row.state}</p>
          <h3>{row.label}</h3>
          <p className="wb-p">{row.why || 'Reported within its window. Nothing to do.'}</p>
        </>
      );
  }
}

function Crew({ who, name, role, line }: { who: string; name: string; role: string; line: string }): JSX.Element {
  return (
    <div className="wb-crew">
      <img src={`/crew-${who}.webp`} alt="" aria-hidden="true" />
      <p>
        <b>{name}</b> <span>{role}</span>
        <br />
        {line}
      </p>
    </div>
  );
}
